import { z } from "zod";
import { ProductEntitySchema } from "./product";
import { ConsultChannelTypeSchema, ProductLightDtoSchema } from "./shop";

/**
 * 상담 채널 enum 의 정의는 shop.ts 에 있다(순환 import 방지).
 * 기존 import 경로(`domain/types/company-shop`)를 깨지 않으려고 여기서 re-export 한다.
 */
export { ConsultChannelTypeSchema };
export type { ConsultChannelType } from "./shop";

export const UpdateShopSettingsCommandSchema = z.object({
  isCarouselLayout: z.boolean().nullable().optional(),
  designProductSlideInterval: z.number().nullable().optional(),
  bestSellerEnabled: z.boolean().nullable().optional(),
  recentlyViewedEnabled: z.boolean().nullable().optional(),
  instagramUrl: z.string().nullable().optional(),
  facebookUrl: z.string().nullable().optional(),
  youtubeUrl: z.string().nullable().optional(),
  naverBlogUrl: z.string().nullable().optional(),
  kakaoChannelUrl: z.string().nullable().optional(),
  /** 실시간 상담 노출 ON/OFF. 채널/링크/키는 회사의 실시간 상담 플러그인이 가진다 */
  consultEnabled: z.boolean().nullable().optional(),
  productDetailLayout: z.enum(["HORIZONTAL", "VERTICAL"]).nullable().optional(),
  /** 나의 디자인(셀프 디자인) 사용 여부 */
  myDesignEnabled: z.boolean().nullable().optional(),
});

export type UpdateShopSettingsRequest = z.infer<
  typeof UpdateShopSettingsCommandSchema
>;

export const ShopBannerEntitySchema = z.object({
  id: z.number(),
  shopId: z.number(),
  title: z.string().nullable().optional(),
  actionType: z.enum(["PRODUCT", "NOTICE", "FAQ", "CUSTOM", "NONE"]),
  actionValue: z.string().nullable().optional(),
  bannerUrl: z.string(),
});

export type ShopBannerEntity = z.infer<typeof ShopBannerEntitySchema>;

export const ShopEntitySchema = z.object({
  id: z.number(),
  companyId: z.number(),
  isCarouselLayout: z.boolean(),
  designProductSlideInterval: z.number().nullable().optional(),
  bestSellerEnabled: z.boolean(),
  recentlyViewedEnabled: z.boolean(),
  instagramUrl: z.string().nullable().optional(),
  facebookUrl: z.string().nullable().optional(),
  youtubeUrl: z.string().nullable().optional(),
  naverBlogUrl: z.string().nullable().optional(),
  kakaoChannelUrl: z.string().nullable().optional(),
  /**
   * 실시간 상담 노출 ON/OFF (쇼핑몰 디자인 > 상담 설정).
   * 구버전 백엔드 응답에는 없다 — 그때는 consultChannelType !== "NONE" 을 ON 으로 본다.
   */
  consultEnabled: z.boolean().optional(),
  /**
   * 쇼핑몰에 실제로 뜨는 상담 채널 (읽기 전용). 백엔드가 consultEnabled 와 회사 플러그인을 AND 해서 내려준다.
   * 백엔드 배포가 MES 보다 늦을 수 있으므로 기본값을 둬서 파싱이 깨지지 않게 한다.
   */
  consultChannelType: ConsultChannelTypeSchema.default("NONE"),
  stripBannerEnabled: z.boolean(),
  stripBannerUrl: z.string().nullable().optional(),
  stripBannerLink: z.string().nullable().optional(),
  productDetailLayout: z.enum(["HORIZONTAL", "VERTICAL"]),
  /**
   * 나의 디자인(셀프 디자인) 사용 여부. 끄면 쇼핑몰의 나의 디자인 메뉴·내 디자인 아이콘을 감추고
   * [다음단계]에서 디자인 방법 선택 없이 파일 업로드 창을 바로 연다.
   * 구버전 백엔드 응답에는 없으므로 지금 모습(켜짐)을 기본값으로 둔다.
   */
  myDesignEnabled: z.boolean().default(true),
});

export type ShopEntity = z.infer<typeof ShopEntitySchema>;

/* ------------------------------------------------------------------ *
 * 쇼핑몰 메인 섹션 (shop_sections)
 * ------------------------------------------------------------------ */

/**
 * PRODUCT_LIST 만 생성/삭제가 가능하고 나머지 6종은 shop 당 1개로 고정이다.
 * 순서 변경과 on/off 만 할 수 있다.
 */
export const ShopSectionTypeSchema = z.enum([
  "BANNER",
  "STRIP_BANNER",
  "RECENTLY_VIEWED",
  "BEST_SELLER",
  "ALL_PRODUCTS",
  "PORTFOLIO",
  "PRODUCT_LIST",
]);

export type ShopSectionType = z.infer<typeof ShopSectionTypeSchema>;

/** 상품 카드 모양. CIRCLE 은 썸네일을 원형으로 자른다 */
export const ShopSectionCardShapeSchema = z.enum(["DEFAULT", "CIRCLE"]);

export type ShopSectionCardShape = z.infer<typeof ShopSectionCardShapeSchema>;

/**
 * 섹션 표시 형식. cardShape(DEFAULT/CIRCLE)는 상품 썸네일을 어떻게 자를지를
 * 정하는 값이라 포트폴리오에는 맞지 않아서 레이아웃 축을 따로 뒀다.
 *
 * GRID 는 현재 렌더링과 동일한 결과여야 한다 — 기존 섹션은 전부 GRID 로 백필된다.
 */
export const ShopSectionLayoutTypeSchema = z.enum(["GRID", "LIST"]);

export type ShopSectionLayoutType = z.infer<typeof ShopSectionLayoutTypeSchema>;

/**
 * 메인 섹션 카드에 필요한 만큼의 포트폴리오.
 *
 * 마크다운 본문(description)·추가 이미지·버튼·연결 주문은 여기 없다.
 * 그것들은 상세 페이지에서만 내려온다 — 메인 섹션 응답이 비대해지는 것을 막는다.
 */
export const ShopSectionPortfolioSchema = z.object({
  id: z.number(),
  title: z.string(),
  summary: z.string().nullable().optional(),
  thumbnailUrl: z.string().nullable().optional(),
  clientName: z.string().nullable().optional(),
  /** 진행 시기 (yyyy-MM-dd) */
  workedAt: z.string().nullable().optional(),
});

export type ShopSectionPortfolio = z.infer<typeof ShopSectionPortfolioSchema>;

export const ShopSectionEntitySchema = z.object({
  id: z.number(),
  sectionType: ShopSectionTypeSchema,
  displayOrder: z.number(),
  isEnabled: z.boolean(),
  /** null 이면 쇼핑몰이 기존 하드코딩 문구를 쓴다 */
  title: z.string().nullable().optional(),
  subtitle: z.string().nullable().optional(),
  cardShape: ShopSectionCardShapeSchema.default("DEFAULT"),
  /*
    백엔드 배포가 MES 보다 늦으면 이 필드가 응답에 없다.
    default 를 둬서 undefined 로 와도 파싱이 깨지지 않게 한다.
  */
  layoutType: ShopSectionLayoutTypeSchema.default("GRID"),
  columnCount: z.number().default(4),
  /** PRODUCT_LIST 일 때만 채워지고 그 외 타입은 빈 배열이다 */
  products: z.array(ProductLightDtoSchema).default([]),
  /** PORTFOLIO 일 때만 채워지고 그 외 타입은 빈 배열이다 */
  portfolios: z.array(ShopSectionPortfolioSchema).default([]),
});

export type ShopSectionEntity = z.infer<typeof ShopSectionEntitySchema>;

export const CreateShopSectionCommandSchema = z.object({
  sectionType: ShopSectionTypeSchema,
  title: z.string().nullable().optional(),
  subtitle: z.string().nullable().optional(),
  cardShape: ShopSectionCardShapeSchema,
  layoutType: ShopSectionLayoutTypeSchema.optional(),
  columnCount: z.number(),
  isEnabled: z.boolean(),
  /** 배열 순서가 곧 display_order 다 */
  productIds: z.array(z.number()),
});

export type CreateShopSectionRequest = z.infer<
  typeof CreateShopSectionCommandSchema
>;

export const UpdateShopSectionCommandSchema = z.object({
  title: z.string().nullable().optional(),
  subtitle: z.string().nullable().optional(),
  cardShape: ShopSectionCardShapeSchema.optional(),
  layoutType: ShopSectionLayoutTypeSchema.optional(),
  columnCount: z.number().optional(),
  isEnabled: z.boolean().optional(),
  /** null 이면 상품 목록을 건드리지 않고, 빈 배열이면 전부 비운다 */
  productIds: z.array(z.number()).nullable().optional(),
});

export type UpdateShopSectionRequest = z.infer<
  typeof UpdateShopSectionCommandSchema
>;

export const UpdateShopSectionOrderCommandSchema = z.object({
  /** 해당 shop 의 모든 섹션이 빠짐없이 들어가야 한다. 누락 시 백엔드가 400 을 낸다 */
  orders: z.array(
    z.object({
      sectionId: z.number(),
      displayOrder: z.number(),
    }),
  ),
});

export type UpdateShopSectionOrderRequest = z.infer<
  typeof UpdateShopSectionOrderCommandSchema
>;

/* ------------------------------------------------------------------ *
 * 헤더 / 푸터 디자인
 * ------------------------------------------------------------------ */

/**
 * 헤더/푸터 메뉴 한 줄.
 * visible 은 기존 노출 조건(consultChannelType !== 'NONE', portfolioEnabled 등)과
 * AND 로 결합한다 — 기존 조건이 false 면 visible: true 여도 쇼핑몰에 뜨지 않는다.
 */
export const ShopMenuItemSchema = z.object({
  key: z.string(),
  label: z.string(),
  visible: z.boolean(),
  displayOrder: z.number(),
  /** 메뉴 링크. 비어 있으면 쇼핑몰이 키별 기본 링크를 쓴다 (책자주문·낱장출력 등 회사마다 다른 메뉴만 쓴다) */
  href: z.string().nullish(),
  /**
   * 회사가 추가한 헤더 메뉴의 종류 (2026-10-26). 기본 메뉴는 없다.
   * CATEGORY · PRODUCT 는 이름과 링크를 그 대상에서 가져오고, LINK 는 이름 · 주소를 회사가 정한다.
   */
  type: z.enum(["CATEGORY", "PRODUCT", "LINK"]).nullish(),
  /** type 이 CATEGORY · PRODUCT 일 때 연결한 카테고리 · 상품 ID */
  targetId: z.number().nullish(),
});

export type ShopMenuItem = z.infer<typeof ShopMenuItemSchema>;
export type ShopMenuItemType = NonNullable<ShopMenuItem["type"]>;

export const ShopLogoAlignmentSchema = z.enum(["LEFT", "CENTER"]);

export type ShopLogoAlignment = z.infer<typeof ShopLogoAlignmentSchema>;

/**
 * presetKey 는 MES 프론트만 아는 표시용 라벨이다. 백엔드는 프리셋 개념이 없고
 * VARCHAR 로 그대로 저장만 하므로, 모르는 값이 와도 파싱이 깨지지 않게 z.string() 으로 둔다.
 */
export const ShopHeaderDesignSchema = z.object({
  presetKey: z.string().default("DEFAULT"),
  // 현재 쇼핑몰 common.css 의 실제 값 (header { background:#fff }, 메뉴 color:#252525)
  backgroundColor: z.string().default("#FFFFFF"),
  textColor: z.string().default("#252525"),
  logoAlignment: ShopLogoAlignmentSchema.default("LEFT"),
  // 현재 쇼핑몰 header 는 position: relative 라 sticky 가 아니다.
  // 기본값을 true 로 두면 기존 헤더 동작이 바뀐다.
  stickyEnabled: z.boolean().default(false),
  menuItems: z.array(ShopMenuItemSchema).default([]),
});

export type ShopHeaderDesign = z.infer<typeof ShopHeaderDesignSchema>;

export const ShopFooterDesignSchema = z.object({
  presetKey: z.string().default("DEFAULT"),
  // 현재 쇼핑몰 common.css 의 실제 값 (footer { background:#f2f2f2 }, color:#252525)
  backgroundColor: z.string().default("#F2F2F2"),
  textColor: z.string().default("#252525"),
  showBusinessInfo: z.boolean().default(true),
  showBusinessHours: z.boolean().default(true),
  showSns: z.boolean().default(true),
  menuItems: z.array(ShopMenuItemSchema).default([]),
});

export type ShopFooterDesign = z.infer<typeof ShopFooterDesignSchema>;

/** PATCH /shop/header — 전달한 필드만 수정한다 (setting 과 달리 부분 수정) */
export const UpdateShopHeaderDesignCommandSchema =
  ShopHeaderDesignSchema.partial();

export type UpdateShopHeaderDesignRequest = z.infer<
  typeof UpdateShopHeaderDesignCommandSchema
>;

/** PATCH /shop/footer — 전달한 필드만 수정한다 */
export const UpdateShopFooterDesignCommandSchema =
  ShopFooterDesignSchema.partial();

export type UpdateShopFooterDesignRequest = z.infer<
  typeof UpdateShopFooterDesignCommandSchema
>;

export const ShopManagementDtoSchema = z.object({
  shop: ShopEntitySchema,
  banners: z.array(ShopBannerEntitySchema),
  bestSellers: z.array(ProductEntitySchema),
  products: z.array(ProductEntitySchema).optional().nullable(),
  // 아래 3개는 백엔드 배포가 MES 보다 늦어도 파싱이 깨지지 않도록 전부 optional 이다.
  // 백엔드가 아직 안 내려주면 MES 는 프리셋 기본값 / 폴백 섹션으로 화면을 구성한다.
  sections: z.array(ShopSectionEntitySchema).optional().nullable(),
  headerDesign: ShopHeaderDesignSchema.optional().nullable(),
  footerDesign: ShopFooterDesignSchema.optional().nullable(),
});

export type ShopManagementDto = z.infer<typeof ShopManagementDtoSchema>;

const UpdateBestsellerCommandSchema = z.object({
  productIds: z.array(z.number()),
});

export type UpdateBestsellerRequest = z.infer<
  typeof UpdateBestsellerCommandSchema
>;

export const CreateBannerRequestSchema = z.object({
  companyId: z.number(),
  command: z.object({
    title: z.string().nullable().optional(),
    actionType: z.enum(["PRODUCT", "NOTICE", "FAQ", "CUSTOM", "NONE"]),
    actionValue: z.string().nullable().optional(),
  }),
  bannerFile: z.instanceof(File),
});

export type CreateBannerRequest = z.infer<typeof CreateBannerRequestSchema>;

export const UpdateBannerRequestSchema = z.object({
  companyId: z.number(),
  bannerId: z.number(),
  command: z.object({
    title: z.string().nullable().optional(),
    actionType: z.enum(["PRODUCT", "NOTICE", "FAQ", "CUSTOM", "NONE"]),
    actionValue: z.string().nullable().optional(),
  }),
  bannerFile: z.any().nullable().optional(),
});

export type UpdateBannerRequest = z.infer<typeof UpdateBannerRequestSchema>;

export const DeleteBannerRequestSchema = z.object({
  companyId: z.number(),
  bannerId: z.number(),
});

export type DeleteBannerRequest = z.infer<typeof DeleteBannerRequestSchema>;

export const UpdateStripBannerRequestSchema = z.object({
  companyId: z.number(),
  command: z.object({
    enabled: z.boolean(),
    link: z.string().nullable().optional(),
  }),
  bannerFile: z.any().nullable().optional(),
});

export type UpdateStripBannerRequest = z.infer<
  typeof UpdateStripBannerRequestSchema
>;
